/**
 * Calculator state — pure mapping between the UI's input state, the engine's
 * rawRow/options arguments, and pipeline rowContexts (§9.2/§9.5/§9.6).
 * No React, no I/O — unit-testable and shared with the golden-case format.
 */

import { TAGS } from '../engineClient';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

export function monthNameFromDate(d) {
  return d instanceof Date && !isNaN(d.getTime()) ? MONTHS[d.getMonth()] : '';
}

/** Fresh calculator state (defaults per §9.2). */
export function emptyCalcState() {
  const today = new Date();
  return {
    loEmail: '',
    lender: '',
    correspondent: false,
    investor: '',
    pricing: 'auto',           // 'auto' | 'LP' | 'BP'
    loanAmount: '',
    revenue: '',
    companyDeal: false,
    brokerRef: false,
    secondaryLoEmail: '',
    manualSecondary: '',
    feeMode: 'auto',           // 'auto' | 'manual'
    feeCharged: true,          // manual mode only
    feeAmount: '',             // manual mode only
    heloc: false,
    processorAssigned: true,
    adder: 'auto',             // 'auto' | 'none' | '2nd' | '3rd'
    monthCount: 1,
    monthCountOverridden: false,
    closingDate: today.toISOString().slice(0, 10), // YYYY-MM-DD input value
    compCode: '',
    client: 'Calculator scenario',
    loadedFromRow: null,       // pipeline rowNum when loaded via §9.5
  };
}

/** UI state → the engine's (rawRow, options, monthCounts) arguments. */
export function stateToEngineInput(state) {
  const closingDate = state.closingDate ? new Date(`${state.closingDate}T12:00:00`) : null;
  const month = monthNameFromDate(closingDate);

  const rawRow = {
    rowNum: state.loadedFromRow || 0,
    client: state.client || 'Calculator scenario',
    loanNumber: '',
    loanAmount: state.loanAmount === '' ? '' : Number(state.loanAmount),
    revenue: state.revenue === '' ? '' : Number(state.revenue),
    lender: state.lender,
    product: state.heloc ? 'HELOC' : 'Conventional - R',
    processor: state.processorAssigned ? 'Assigned' : '',
    month,
    closingDate,
    compCode: state.compCode || '',
    correspondentCol: state.correspondent ? 'Correspondent' : '',
    investor: state.correspondent ? state.investor : '',
    minNumber: '',
    primaryLo: state.loEmail,
    secondaryLo: state.secondaryLoEmail,
    primaryCommissionCell: '',
    secondaryCommissionCell: state.manualSecondary === '' ? '' : Number(state.manualSecondary),
    compFormulaUsed: '',
    dealSourceCol: state.companyDeal ? 'Company' : state.brokerRef ? 'Broker Referral' : '',
    payrollBatch: '',
    payrollFlags: '',
  };

  const options = {};
  if (state.pricing === 'LP' || state.pricing === 'BP') options.pricingOverride = state.pricing;
  if (state.feeMode === 'manual') {
    options.feeOverride = state.feeCharged
      ? { charged: true, amount: state.feeAmount === '' ? 0 : Number(state.feeAmount) }
      : { charged: false };
  }
  if (state.adder !== 'auto') options.submissionAdder = state.adder;

  const monthCounts = state.loEmail && month
    ? { [`${state.loEmail}|${month}`]: Number(state.monthCount) || 1 }
    : {};

  return { rawRow, options, monthCounts };
}

/** Pipeline rowContext (wire form, §9.5) → calculator state. */
export function pipelineRowToState(match) {
  const raw = match.rawRow;
  const closing = raw.closingDate ? new Date(raw.closingDate) : null;
  const dealCol = String(raw.dealSourceCol || '').toUpperCase();
  const product = String(raw.product || '');
  const compCode = String(raw.compCode || '');
  const lender = String(raw.lender || '').trim();

  // The Correspondent checkbox mirrors the engine's isCorr predicate
  // (engine/pricingContext.js) — the sheet column holds "Correspondent",
  // "Wholesale", or blank, so a non-empty value must NOT mean yes:
  //   1. an explicit column value is authoritative ('correspondent' → yes,
  //      anything else, e.g. 'Wholesale' → no),
  //   2. Lender = Future Home Loans always means correspondent,
  //   3. the comp-code CORR tag / Min Number decide only when the column
  //      is EMPTY.
  const corrCol = String(raw.correspondentCol || '').trim().toLowerCase();
  const correspondent = corrCol === 'correspondent' ||
    lender.toLowerCase() === 'future home loans' ||
    (!corrCol && (TAGS.CORRESPONDENT.test(compCode) ||
      (raw.minNumber !== '' && raw.minNumber !== null && raw.minNumber !== undefined)));

  return {
    ...emptyCalcState(),
    loEmail: String(raw.primaryLo || '').trim().toLowerCase(),
    lender,
    correspondent,
    investor: String(raw.investor || '').trim(),
    loanAmount: raw.loanAmount === '' || raw.loanAmount === null ? '' : Number(raw.loanAmount),
    revenue: raw.revenue === '' || raw.revenue === null ? '' : Number(raw.revenue),
    companyDeal: /COMPANY|^CD$/.test(dealCol),
    brokerRef: /BROKER|REFERRAL/.test(dealCol),
    secondaryLoEmail: String(raw.secondaryLo || '').trim().toLowerCase(),
    manualSecondary: raw.secondaryCommissionCell === '' || raw.secondaryCommissionCell === null ? '' : Number(raw.secondaryCommissionCell),
    heloc: /heloc|heloan|hecm/i.test(product) || /heloc|heloan|hecm/i.test(compCode),
    processorAssigned: String(raw.processor || '').trim() !== '',
    monthCount: match.monthCount || 1,
    monthCountOverridden: true, // keep the live count that came with the row
    closingDate: closing && !isNaN(closing.getTime()) ? closing.toISOString().slice(0, 10) : emptyCalcState().closingDate,
    compCode,
    client: String(raw.client || ''),
    loadedFromRow: raw.rowNum,
  };
}

/** Serializable golden-case row_context (state + effective engine inputs). */
export function stateToRowContext(state) {
  const { rawRow, options, monthCounts } = stateToEngineInput(state);
  return {
    state,
    rawRow: {
      ...rawRow,
      closingDate: rawRow.closingDate instanceof Date && !isNaN(rawRow.closingDate.getTime())
        ? rawRow.closingDate.toISOString()
        : null,
    },
    options,
    monthCounts,
  };
}

/** Golden-case / stored rowContext → live engine arguments (Date revival). */
export function rowContextToEngineInput(rowContext) {
  return {
    rawRow: {
      ...rowContext.rawRow,
      closingDate: rowContext.rawRow.closingDate ? new Date(rowContext.rawRow.closingDate) : null,
    },
    options: rowContext.options || {},
    monthCounts: rowContext.monthCounts || {},
  };
}
